import { UserManager, WebStorageStateStore } from "oidc-client-ts";

/**
 * Single shared OIDC client for the app.
 *
 * It is created here rather than inside <AuthProvider> so that non-React code
 * (services/api.js, services/auth.js) can reach the same instance — React hooks
 * are only callable while a component is rendering, and those modules are plain
 * functions. The provider is handed this same object, so both worlds read one
 * set of tokens.
 */

const authority = `${process.env.REACT_APP_KEYCLOAK_URL}/realms/${process.env.REACT_APP_KEYCLOAK_REALM}`;

export const oidcConfig = {
  authority,
  client_id: process.env.REACT_APP_KEYCLOAK_CLIENT_ID,
  // Derived rather than configured: correct in every environment, and Keycloak
  // constrains the accepted values through the client's redirect URIs.
  redirect_uri: window.location.origin,
  post_logout_redirect_uri: window.location.origin,
  response_type: "code",
  scope: "openid profile email",
  // Renew before the 5-minute access token expires, so an active volunteer is
  // never interrupted mid-shift.
  automaticSilentRenew: true,
  // Renew 30s before expiry. This must stay comfortably below the realm's
  // accessTokenLifespan (300s): renewal is triggered once the remaining
  // lifetime drops under this threshold, so if the two ever meet, every freshly
  // issued token is born "expiring" and the SPA renews in a tight loop. The
  // library's default is 60s, which would do exactly that against a 60s
  // lifespan — observed in local testing, and it exhausted the backend's
  // connections to Keycloak.
  accessTokenExpiringNotificationTimeInSeconds: 30,
  // localStorage rather than sessionStorage, so a second tab shares the session
  // instead of sending the volunteer through login again.
  userStore: new WebStorageStateStore({ store: window.localStorage }),
};

export const userManager = new UserManager(oidcConfig);

/**
 * Where AuthGate parks the path the visitor originally asked for, so it can be
 * restored after Keycloak returns them to the origin.
 */
export const RETURN_TO_KEY = "copline.oidc.returnTo";

/**
 * Runs after Keycloak redirects back with an authorization code, dropping the
 * ?code=&state= query so it doesn't linger in the address bar or in shared
 * links. Restoring the original path is AuthGate's job — it has to happen
 * before the router mounts, which is earlier than this callback resolves.
 */
export function onSigninCallback() {
  window.history.replaceState({}, document.title, window.location.pathname);
}

let signOutInProgress = false;

/**
 * True from the moment a sign-out starts until the browser leaves the page.
 *
 * AuthGate has to know about this. `signoutRedirect()` removes the user from
 * the store *before* it navigates to Keycloak, which re-renders the app as
 * unauthenticated — and the gate would then race the sign-out by starting a new
 * sign-in. Keycloak still holds the SSO cookie at that point, so it would hand
 * back a fresh session and the user would never actually be logged out.
 *
 * `useAuth().activeNavigator` does not cover this: it is only set when sign-out
 * goes through the hook, and logout() is called from plain modules that cannot
 * use hooks.
 */
export const isSignOutInProgress = () => signOutInProgress;

/**
 * Ends the Keycloak SSO session, not just the local one.
 */
export function signOut() {
  signOutInProgress = true;
  return userManager.signoutRedirect();
}

/**
 * The Authorization header to send with an API request, or `{}` when there is
 * no token to send.
 *
 * The single place that knows how to obtain a token, so the request helpers
 * can't drift apart. Read fresh on every call rather than cached: the token is
 * short-lived and gets renewed underneath us, and a stale copy would start
 * producing 401s the moment it is replaced.
 */
export async function authHeaders() {
  const user = await userManager.getUser();
  if (!user || !user.access_token) return {};
  return { Authorization: `Bearer ${user.access_token}` };
}

/**
 * Header the backend sets (AuthGuard) only when the session policy ended the
 * session — the 24h cap or the pre-shift cutoff. Its presence is what separates
 * "this user is finished" from "the access token simply expired".
 */
export const SESSION_END_HEADER = "X-Session-End-Reason";

let refreshInFlight = null;

/**
 * Renews the access token, resolving to whether a usable token was obtained.
 *
 * Deliberately single-flight: a page that fires ten requests at once will get
 * ten simultaneous 401s when the token expires, and each one must not launch
 * its own renewal. They all await the same promise instead.
 */
export function refreshSession() {
  if (!refreshInFlight) {
    refreshInFlight = userManager
      .signinSilent()
      .then((user) => Boolean(user && user.access_token))
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }

  return refreshInFlight;
}
